import React, { useState, useRef, useCallback } from "react";
import { useModelStore } from "@/store/useModelStore";
import {
  TrashIcon,
  ArrowUpOnSquareIcon,
  ArrowDownOnSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/solid";
import {
  importNodesFromCSV,
  exportNodesToCSV,
  copyNodesToClipboard,
  processExcelFile,
  createFileValidationError,
  generateFilename,
} from "@/utilities/CSVUtils";
import { CSVImportChoiceDialog } from "../dialogs/CSVImportChoiceDialog";
import { DuplicateGroupReviewDialog } from "../dialogs/DuplicateGroupReviewDialog";
import { useEnhancedCSVImport } from "@/hooks/useEnhancedCSVImport";
import { TableLayout, TableColumn } from "../../workspace/components/ui/TableLayout";

export const NodesDataTable: React.FC = () => {
  const nodes = useModelStore((state) => state.nodes);
  const addNode = useModelStore((state) => state.addNode);
  const deleteNode = useModelStore((state) => state.deleteNode);
  const setNodes = useModelStore((state) => state.setNodes);
  const updateNode = useModelStore((state) => state.updateNode);
  const selectedNodeIds = useModelStore((state) => state.selectedNodeIds);
  const toggleNodeSelection = useModelStore(
    (state) => state.toggleNodeSelection
  );

  const [newCoordinates, setNewCoordinates] = useState({ x: "", y: "", z: "" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inline cell editing state
  const [editState, setEditState] = useState<{
    nodeId: string;
    axis: "x" | "y" | "z";
    value: string;
  } | null>(null);

  // Enhanced CSV import hook
  const csvImport = useEnhancedCSVImport(
    nodes,
    (importedNodes, mode) => {
      console.log("🎯 NodesDataTable onImportComplete:", {
        mode,
        importedNodesCount: importedNodes.length,
        existingNodesCount: nodes.length,
      });

      setNodes(importedNodes);

      const message =
        mode === "replace" || nodes.length === 0
          ? `Import completed successfully! ${importedNodes.length} nodes imported.`
          : `Import completed successfully! ${importedNodes.length} total nodes (${importedNodes.length - nodes.length} new).`;

      alert(message);
    },
    "nodes"
  );

  const handleCoordinateChange = (axis: "x" | "y" | "z", value: string) => {
    setNewCoordinates((prev) => ({ ...prev, [axis]: value }));
  };

  const handleAddNode = () => {
    const x = parseFloat(newCoordinates.x);
    const y = parseFloat(newCoordinates.y);
    const z = parseFloat(newCoordinates.z);

    if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
      const exists = nodes.some((n) => n.x === x && n.y === y && n.z === z);
      if (exists) {
        alert("A node with these coordinates already exists!");
        return;
      }
      addNode({ x, y, z });
      setNewCoordinates({ x: "", y: "", z: "" });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleAddNode();
    }
  };

  const handleExport = useCallback(() => {
    const filename = generateFilename("nodes");
    exportNodesToCSV(nodes, { filename });
  }, [nodes]);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      try {
        let result;

        if (fileExtension === "csv") {
          result = await importNodesFromCSV(file);
        } else if (fileExtension === "xlsx" || fileExtension === "xls") {
          const excelResult = await processExcelFile(file);
          if (excelResult.success) {
            const transformedData = excelResult.data.map(
              (node: any, index: number) => ({
                id: node.id?.toString() || (index + 1).toString(),
                x:
                  typeof node.x === "number" ? node.x : parseFloat(node.x) || 0,
                y:
                  typeof node.y === "number" ? node.y : parseFloat(node.y) || 0,
                z:
                  typeof node.z === "number" ? node.z : parseFloat(node.z) || 0,
                isSelected: false,
                color: "#000000",
              })
            );
            result = {
              ...excelResult,
              data: transformedData,
              preview: transformedData.slice(0, 5),
            };
          } else {
            result = excelResult;
          }
        } else {
          alert(createFileValidationError(file, ["csv", "xlsx", "xls"]));
          return;
        }

        csvImport.handleFileImported(result);
      } catch (error) {
        console.error("File processing error:", error);
        alert("An error occurred while processing the file. Please try again.");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [csvImport]
  );

  const handleCopy = useCallback(async () => {
    const success = await copyNodesToClipboard(nodes);
    if (success) {
      alert("Nodes copied to clipboard!");
    } else {
      alert("Failed to copy to clipboard. Please try again.");
    }
  }, [nodes]);

  const startEdit = (
    e: React.MouseEvent,
    nodeId: string,
    axis: "x" | "y" | "z",
    current: number
  ) => {
    e.stopPropagation();
    setEditState({ nodeId, axis, value: current.toString() });
  };

  const commitEdit = () => {
    if (!editState) return;
    const num = parseFloat(editState.value);
    if (!Number.isFinite(num)) {
      setEditState(null);
      return;
    }
    const rounded = Math.round(num * 1000) / 1000;
    updateNode(editState.nodeId, { [editState.axis]: rounded } as any);
    setEditState(null);
  };

  const cancelEdit = () => setEditState(null);

  // ---------- TABLE COLUMNS DEFINITION ----------
  const columns: TableColumn[] = [
    {
      colname: "ID",
      className: "w-1/4",
      render: (row: any) => row.id,
    },
    {
      colname: "X",
      className: "w-1/4",
      render: (row: any) =>
        editState &&
        editState.nodeId === row.id &&
        editState.axis === "x" ? (
          <input
            type="text"
            value={editState.value}
            autoFocus
            className="w-full h-8 px-2 border rounded"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              setEditState((prev) =>
                prev ? { ...prev, value: e.target.value } : prev
              )
            }
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") cancelEdit();
            }}
          />
        ) : (
          <span
            className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text"
            onClick={(e) => startEdit(e, row.id, "x", row.x)}
          >
            {row.x.toFixed(2)}
          </span>
        ),
    },
    {
      colname: "Y",
      className: "w-1/4",
      render: (row: any) =>
        editState &&
        editState.nodeId === row.id &&
        editState.axis === "y" ? (
          <input
            type="text"
            value={editState.value}
            autoFocus
            className="w-full h-8 px-2 border rounded"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              setEditState((prev) =>
                prev ? { ...prev, value: e.target.value } : prev
              )
            }
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") cancelEdit();
            }}
          />
        ) : (
          <span
            className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text"
            onClick={(e) => startEdit(e, row.id, "y", row.y)}
          >
            {row.y.toFixed(2)}
          </span>
        ),
    },
    {
      colname: "Z",
      className: "w-1/4",
      render: (row: any) =>
        editState &&
        editState.nodeId === row.id &&
        editState.axis === "z" ? (
          <input
            type="text"
            value={editState.value}
            autoFocus
            className="w-full h-8 px-2 border rounded"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              setEditState((prev) =>
                prev ? { ...prev, value: e.target.value } : prev
              )
            }
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") cancelEdit();
            }}
          />
        ) : (
          <span
            className="inline-flex items-center h-8 w-full px-2 bg-gray-50 rounded cursor-text"
            onClick={(e) => startEdit(e, row.id, "z", row.z)}
          >
            {row.z.toFixed(2)}
          </span>
        ),
    },
    {
      colname: "Action",
      className: "w-[60px]",
      render: (row: any) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteNode(row.id);
          }}
          className="text-red-500 hover:text-red-700 transition-colors"
          title="Delete node"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <>
      <TableLayout
        title="Nodes"
        theader={columns}
        tdata={nodes}
        actions={
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-1 hover:bg-gray-600 rounded"
              title="Import from CSV"
            >
              <ArrowDownOnSquareIcon className="h-5 w-5" />
            </button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelected}
              accept=".csv,.xlsx,.xls"
              style={{ display: "none" }}
            />

            <button
              onClick={handleExport}
              className="p-1 hover:bg-gray-600 rounded"
              title="Export to CSV"
            >
              <ArrowUpOnSquareIcon className="h-5 w-5" />
            </button>

            <button
              onClick={handleCopy}
              className="p-1 hover:bg-gray-600 rounded"
              title="Copy to Clipboard"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
            </button>
          </>
        }
        getRowClassName={(row: any) =>
          selectedNodeIds.includes(row.id)
            ? "selected-table-row bg-blue-100"
            : ""
        }
        onRowClick={(row: any) => toggleNodeSelection(row.id)}
        extra={
          <table className="w-full table-fixed">
            <tbody>
              <tr>
                <td className="w-1/5 px-3 py-2 text-sm text-black">New</td>
                <td className="w-1/4 px-3 py-2">
                  <input
                    type="text"
                    value={newCoordinates.x}
                    onChange={(e) =>
                      handleCoordinateChange("x", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                    className="w-full px-2 py-1 text-sm text-black border rounded bg-white"
                    placeholder="X"
                  />
                </td>

                <td className="w-1/4 px-3 py-2">
                  <input
                    type="text"
                    value={newCoordinates.y}
                    onChange={(e) =>
                      handleCoordinateChange("y", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                    className="w-full px-2 py-1 text-sm text-black border rounded bg-white"
                    placeholder="Y"
                  />
                </td>

                <td className="w-1/4 px-3 py-2">
                  <input
                    type="text"
                    value={newCoordinates.z}
                    onChange={(e) =>
                      handleCoordinateChange("z", e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                    className="w-full px-2 py-1 text-sm text-black border rounded bg-white"
                    placeholder="Z"
                  />
                </td>

                <td className="w-[75px] px-3 py-2">
                  <button
                    onClick={handleAddNode}
                    className="w-full px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        }
      />

      {/* CSV Dialogs */}
      <CSVImportChoiceDialog
        isOpen={csvImport.showChoiceDialog}
        onClose={csvImport.cancelImport}
        onChoice={csvImport.handleImportChoice}
        title="Import Nodes"
        itemCount={csvImport.importResult.data.length}
        itemType="nodes"
      />

      <DuplicateGroupReviewDialog
        isOpen={csvImport.showDuplicateReview}
        onClose={csvImport.cancelImport}
        onFinish={csvImport.handleDuplicateResolution}
        groups={csvImport.duplicateGroups?.groups || []}
        itemType="nodes"
        title="Review Duplicate Nodes"
      />

      {/* Error */}
      {csvImport.hasError && csvImport.importResult.errors.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              Import Error
            </h3>
            <p className="text-gray-700 mb-4">
              {csvImport.importResult.errors.join(", ")}
            </p>
            <div className="flex justify-end">
              <button
                onClick={csvImport.cancelImport}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Loader */}
      {csvImport.isProcessing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-700">
                Processing import and checking for duplicates...
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
